/**
 * Asset Actions Hook
 *
 * Manages action handlers for asset operations including:
 * - Comment submission
 * - Keyboard shortcuts for status changes
 * - Integration with asset action handlers
 *
 * Risk Level: Medium-High
 * - Async operations
 * - Multiple external dependencies
 * - Requires coordination with modal state
 */

import { useCallback } from 'react';
import { toast } from 'sonner';

import type { PartialAsset } from '@/hooks/useAssets';
import { useUpdateAsset } from '@/hooks/useAssets';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useEntitlements } from '@/hooks/useEntitlements';
import { getStatusFromKeyboardCode } from '@/sections/asset/constants';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import { parseKeys } from '@/sections/SearchByType';
import type { Asset, AssetStatus } from '@/types';

/**
 * Parameters for useAssetActions hook
 */
interface UseAssetActionsParams {
  /** Function to invalidate/refetch asset data */
  invalidateAssets: () => void;
  /** Currently selected assets */
  selectedAssets: Asset[];
  /** Number of selected rows (for toast messages) */
  selectedRowsCount: number;
  /** Function to set confirm status modal state */
  setConfirmStatus: (
    state: {
      status: AssetStatus;
      data: PartialAsset[];
    } | null
  ) => void;
  /**
   * Current bulk selection state — forwarded to useAssetStatusHandlers
   * for bulk path detection (mode='all' or manual > BULK_THRESHOLD).
   */
  selectionState?: BulkSelectionState;
  /**
   * Called by useAssetStatusHandlers when the bulk path is triggered.
   * Caller (index.tsx) opens the BulkConfirmationDialog and submits the job.
   */
  onBulkRequested?: (status: AssetStatus) => void;
}

/**
 * Return type for useAssetActions hook
 */
interface AssetActionsReturn {
  /** Handlers from useAssetActionHandlers */
  handlers: ReturnType<typeof useAssetActionHandlers>;
  /** Handler for adding comments to selected assets */
  handleComment: (comment: string) => Promise<void>;
  /** Handler for keyboard shortcuts (Alt+F, Alt+U, Alt+D) */
  handleKeyboardAction: (code: string, data: Asset[]) => void;
}

/**
 * Hook to manage asset action handlers
 *
 * Provides handlers for:
 * - Adding comments to assets
 * - Keyboard shortcuts for status changes
 * - Integration with existing asset action handlers
 *
 * @param params - Configuration object with dependencies
 * @returns Object containing action handlers
 *
 * @example
 * ```tsx
 * const { handlers, handleComment, handleKeyboardAction } = useAssetActions({
 *   invalidateAssets,
 *   selectedAssets,
 *   selectedRowsCount: selectedRows.length,
 *   setConfirmStatus,
 * });
 *
 * // Use in comment modal
 * <CommentModal onSubmit={handleComment} />
 *
 * // Use in keyboard actions
 * <Table keyboardActions={{
 *   onKeyboardAction: handleKeyboardAction
 * }} />
 * ```
 */
export const useAssetActions = ({
  invalidateAssets,
  selectedAssets,
  selectedRowsCount: _selectedRowsCount,
  setConfirmStatus,
  selectionState,
  onBulkRequested,
}: UseAssetActionsParams): AssetActionsReturn => {
  // Get mutation function for updating assets
  const { mutateAsync: updateAsset } = useUpdateAsset();

  // Get existing asset action handlers — pass bulk context through
  const handlers = useAssetActionHandlers({ selectionState, onBulkRequested });

  // Get entitlement gate for write access
  const { canWriteAssets } = useEntitlements();

  /**
   * Handles adding comments to selected assets
   *
   * Adds the same comment to all selected assets in parallel,
   * handles partial failures gracefully, and shows accurate feedback.
   *
   * Uses Promise.allSettled to ensure all operations complete even if some fail,
   * providing accurate user feedback about successes and failures.
   *
   * @param comment - The comment text to add to assets
   */
  const handleComment = useCallback(
    async (comment: string) => {
      const promises = selectedAssets.map(selectedAsset => {
        return updateAsset({
          key: selectedAsset.key,
          name: parseKeys.assetKey(selectedAsset.key).identifier,
          comment,
          status: selectedAsset.status, // Use cached status to avoid N GET requests
          showSnackbar: false, // Suppress individual toasts, show bulk summary instead
        });
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        invalidateAssets();
      }

      // Show accurate feedback based on results
      if (succeeded > 0 && failed === 0) {
        toast.success(
          `Note added to ${succeeded} asset${succeeded > 1 ? 's' : ''}`
        );
      } else if (succeeded > 0 && failed > 0) {
        toast.warning(
          `Note added to ${succeeded} asset${succeeded > 1 ? 's' : ''}, ` +
            `but failed for ${failed} asset${failed > 1 ? 's' : ''}`
        );
      } else {
        toast.error(
          `Failed to add note to ${failed} asset${failed > 1 ? 's' : ''}`
        );
      }

      // Log failures for debugging
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            asset: selectedAssets[i]?.identifier || selectedAssets[i]?.key,
            error: r.reason,
          }));
        console.error(
          '[useAssetActions] Failed to add comment to assets:',
          failedResults
        );
      }
    },
    [selectedAssets, updateAsset, invalidateAssets]
  );

  /**
   * Handles keyboard shortcuts for bulk status changes
   *
   * Maps keyboard codes to asset statuses:
   * - KeyF: Freeze assets
   * - KeyU: Unfreeze/Activate assets
   * - KeyD: Delete assets
   *
   * All shortcuts are gated only by the write-asset entitlement. Approving /
   * activating / unfreezing is customer self-serve — the old Praetorian-only
   * block on KeyU was removed when the backend dropped that restriction.
   *
   * @param code - Keyboard code (e.g., 'KeyF', 'KeyU', 'KeyD')
   * @param data - Array of assets to update
   */
  const handleKeyboardAction = useCallback(
    (code: string, data: Asset[]) => {
      // Block keyboard actions without write permission
      if (!canWriteAssets) return;

      const status = getStatusFromKeyboardCode(code);
      if (status) {
        setConfirmStatus({
          status,
          data,
        });
      }
    },
    [setConfirmStatus, canWriteAssets]
  );

  return {
    handlers,
    handleComment,
    handleKeyboardAction,
  };
};
